import { useState, useCallback } from 'react';

/**
 * Hook for managing drag-and-drop behavior of tree items.
 * Supports:
 * - Nesting items (drop on middle of target)
 * - Reordering items (drop on top/bottom edge of target)
 * - Child drop zones (drop at end of children list)
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.sprint - Current sprint/item data
 * @param {Array} options.allSprints - All sprints for ancestry checks
 * @param {Array} options.childSprints - Direct children of this item
 * @param {number} options.depth - Nesting depth (0 = top-level)
 * @param {string|null} options.parentSprintId - Parent ID for nested items
 * @param {string|null} options.draggedSprintId - Currently dragged item ID (from parent)
 * @param {Function} options.onNestCampaign - Handler for nesting
 * @param {Function} options.onReorderCampaign - Handler for reordering
 * @param {Function} options.onDragStart - Parent drag start handler
 * @param {Function} options.onDragEnd - Parent drag end handler
 * @param {React.RefObject} options.itemRef - Ref to the item element
 * @returns {Object} Drag state and event handlers
 */
const useTreeItemDragDrop = ({
  sprint,
  allSprints,
  childSprints,
  siblingOrder, // Array of sibling IDs in current display order (for nested items)
  depth,
  parentSprintId,
  draggedSprintId,
  onNestCampaign,
  onReorderCampaign,
  onReorderNestedCampaign,
  onDragStart: parentDragStart,
  onDragEnd: parentDragEnd,
  itemRef,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [reorderPosition, setReorderPosition] = useState(null); // 'above' | 'below' | null
  const [isChildDropZoneActive, setIsChildDropZoneActive] = useState(false);

  const isBeingDragged = draggedSprintId === sprint.id;

  // Check if we can accept a drop (can't nest into self or own descendants)
  const canAcceptDrop = useCallback((draggedId) => {
    if (!draggedId || draggedId === sprint.id) return false;
    
    // Check if sprint is a descendant of the dragged item (would create circular ref)
    // Build ancestor chain to check for circular reference
    const isDescendantOfDragged = (item, targetId) => {
      let currentItem = item;
      while (currentItem?.parentSprintId) {
        if (currentItem.parentSprintId === targetId) return true;
        currentItem = allSprints.find(s => s.id === currentItem.parentSprintId);
      }
      return false;
    };
    
    if (isDescendantOfDragged(sprint, draggedId)) return false;
    
    // Also check if dragged is already a direct child
    if (childSprints.some(c => c.id === draggedId)) return false;
    
    return true;
  }, [sprint, allSprints, childSprints]);

  // Determine drop zone based on cursor position
  const getDropZone = useCallback((e) => {
    if (!itemRef.current) return 'middle';
    
    const rect = itemRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;
    
    // Top 25% = reorder above, Bottom 25% = reorder below, Middle 50% = nest
    if (y < height * 0.25) return 'above';
    if (y > height * 0.75) return 'below';
    return 'middle';
  }, [itemRef]);

  const handleDragStart = useCallback((e) => {
    e.dataTransfer.setData('application/fokus-campaign', sprint.id);
    e.dataTransfer.effectAllowed = 'move';
    
    // Use a transparent 1x1 pixel as drag image
    // The actual visual feedback is provided by the drop target (flowchart shows a preview box)
    const emptyImg = new Image();
    emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(emptyImg, 0, 0);
    
    parentDragStart?.(sprint.id);
  }, [sprint.id, parentDragStart]);

  const handleDragEnd = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    setReorderPosition(null);
    parentDragEnd?.();
  }, [parentDragEnd]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    const draggedId = e.dataTransfer.types.includes('application/fokus-campaign') 
      ? draggedSprintId 
      : null;
    
    if (!draggedId || draggedId === sprint.id) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    
    const zone = getDropZone(e);
    
    // Edge zones (above/below) = reorder or nest into parent
    // Middle zone = nest into this item
    if (zone === 'above' || zone === 'below') {
      e.dataTransfer.dropEffect = 'move';
      setReorderPosition(zone);
      setIsDragOver(false);
    } else if (zone === 'middle' && canAcceptDrop(draggedId)) {
      // Drop on middle = nest into this item
      e.dataTransfer.dropEffect = 'move';
      setIsDragOver(true);
      setReorderPosition(null);
    } else {
      // Can't nest into this item (e.g., would create circular ref)
      // Fall back to showing as reorder below
      e.dataTransfer.dropEffect = 'move';
      setReorderPosition('below');
      setIsDragOver(false);
    }
  }, [sprint.id, draggedSprintId, getDropZone, canAcceptDrop]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    // Only clear if we're actually leaving this element (not entering a child)
    if (!itemRef.current?.contains(e.relatedTarget)) {
      setIsDragOver(false);
      setReorderPosition(null);
    }
  }, [itemRef]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const draggedId = e.dataTransfer.getData('application/fokus-campaign');
    if (!draggedId || draggedId === sprint.id) {
      setIsDragOver(false);
      setReorderPosition(null);
      return;
    }
    
    const zone = getDropZone(e);
    const draggedSprint = allSprints.find(s => s.id === draggedId);
    
    if (zone === 'middle' && canAcceptDrop(draggedId)) {
      // Drop on middle = nest into this campaign
      if (draggedSprint && onNestCampaign) {
        onNestCampaign(draggedSprint.id, sprint.id, draggedSprint.name);
      }
    } else if (zone === 'above' || zone === 'below') {
      // Drop on edge = either reorder (top-level) or reorder within parent (nested items)
      if (depth === 0) {
        // Dropping on a top-level item's edge
        // If the dragged item is nested, we need to unnest it first
        if (draggedSprint?.parentSprintId && onNestCampaign) {
          // Unnest the campaign (pass null as target to promote to root)
          onNestCampaign(draggedSprint.id, null, draggedSprint.name);
        }
        // Then reorder among top-level campaigns
        if (onReorderCampaign) {
          onReorderCampaign(draggedId, sprint.id, zone === 'above' ? 'before' : 'after');
        }
      } else if (parentSprintId && draggedSprint) {
        // Nested item: dropping between siblings
        // Check if dragged item is already a sibling (same parent)
        if (draggedSprint.parentSprintId === parentSprintId) {
          // Same parent - just reorder within the parent's childOrder
          if (onReorderNestedCampaign) {
            // Pass the current visible order so the handler knows exactly what the user sees
            onReorderNestedCampaign(draggedId, sprint.id, zone === 'above' ? 'before' : 'after', parentSprintId, siblingOrder);
          }
        } else {
          // Different parent - need to move to this parent first, then reorder
          // First nest into the new parent
          if (onNestCampaign) {
            onNestCampaign(draggedSprint.id, parentSprintId, draggedSprint.name);
          }
          // Then reorder (the nesting will add it to the end, so we need to reorder)
          // Note: This happens async, so the reorder might need to wait
          // For now, just nest - the item will appear at the end
          // TODO: Could chain these operations for precise positioning
        }
      }
    } else {
      // Fallback: try reorder for top-level
      if (depth === 0) {
        if (draggedSprint?.parentSprintId && onNestCampaign) {
          onNestCampaign(draggedSprint.id, null, draggedSprint.name);
        }
        if (onReorderCampaign) {
          onReorderCampaign(draggedId, sprint.id, 'after');
        }
      }
    }
    
    setIsDragOver(false);
    setReorderPosition(null);
    parentDragEnd?.();
  }, [
    sprint.id, 
    allSprints, 
    depth, 
    parentSprintId, 
    getDropZone, 
    canAcceptDrop,
    onNestCampaign, 
    onReorderCampaign,
    onReorderNestedCampaign,
    parentDragEnd
  ]);

  // Child drop zone handlers
  const handleChildDropZoneDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsChildDropZoneActive(true);
  }, []);

  const handleChildDropZoneDragLeave = useCallback(() => {
    setIsChildDropZoneActive(false);
  }, []);

  const handleChildDropZoneDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const draggedId = e.dataTransfer.getData('application/fokus-campaign');
    if (draggedId && draggedId !== sprint.id) {
      const draggedSprint = allSprints.find(s => s.id === draggedId);
      if (draggedSprint && onNestCampaign) {
        onNestCampaign(draggedSprint.id, sprint.id, draggedSprint.name);
      }
    }
    setIsChildDropZoneActive(false);
    parentDragEnd?.();
  }, [sprint.id, allSprints, onNestCampaign, parentDragEnd]);

  return {
    // Drag state
    isDragOver,
    reorderPosition,
    isBeingDragged,
    isChildDropZoneActive,
    canAcceptDrop,
    
    // Item drag handlers
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    
    // Child drop zone handlers
    handleChildDropZoneDragOver,
    handleChildDropZoneDragLeave,
    handleChildDropZoneDrop,
  };
};

export default useTreeItemDragDrop;
